import React, { useState, useEffect } from 'react';
import axios from 'axios';

const StatusMappings = () => {
  const [mappings, setMappings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [editingId, setEditingId] = useState(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [formData, setFormData] = useState({
    crm_status: '',
    client_status: '',
    description: '',
    is_active: true,
    faq_tag: '',
    display_order: 0
  });

  useEffect(() => {
    fetchMappings();
  }, []);

  const fetchMappings = async () => {
    try {
      const token = localStorage.getItem('adminToken');
      const response = await axios.get('/api/admin/status-mappings', {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMappings(response.data);
      setLoading(false);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load status mappings');
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    try {
      const token = localStorage.getItem('adminToken');

      if (editingId) {
        // Update existing
        await axios.put(`/api/admin/status-mappings/${editingId}`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
      } else {
        // Create new
        await axios.post('/api/admin/status-mappings', formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
      }

      // Reset form
      setFormData({
        crm_status: '',
        client_status: '',
        description: '',
        is_active: true,
        faq_tag: '',
        display_order: 0
      });
      setEditingId(null);
      setShowAddForm(false);

      // Refresh list
      fetchMappings();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to save status mapping');
    }
  };

  const handleEdit = (mapping) => {
    setFormData({
      crm_status: mapping.crm_status,
      client_status: mapping.client_status,
      description: mapping.description || '',
      is_active: mapping.is_active,
      faq_tag: mapping.faq_tag || '',
      display_order: mapping.display_order || 0
    });
    setEditingId(mapping.id);
    setShowAddForm(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this status mapping?')) {
      return;
    }

    try {
      const token = localStorage.getItem('adminToken');
      await axios.delete(`/api/admin/status-mappings/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchMappings();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to delete status mapping');
    }
  };

  const handleCancel = () => {
    setFormData({
      crm_status: '',
      client_status: '',
      description: '',
      is_active: true,
      faq_tag: '',
      display_order: 0
    });
    setEditingId(null);
    setShowAddForm(false);
    setError('');
  };

  if (loading) {
    return <div style={{ textAlign: 'center', padding: '2rem' }}>Loading...</div>;
  }

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '2rem' }}>
        <h1 style={{ margin: '0', color: '#333' }}>Status Mappings</h1>
        {!showAddForm && (
          <button
            onClick={() => setShowAddForm(true)}
            style={{
              padding: '0.75rem 1.5rem',
              background: '#4A90E2',
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              cursor: 'pointer',
              fontWeight: '500'
            }}
          >
            Add New Mapping
          </button>
        )}
      </div>

      {error && (
        <div style={{
          padding: '1rem',
          background: '#ffebee',
          color: '#c62828',
          borderRadius: '6px',
          marginBottom: '1rem'
        }}>
          {error}
        </div>
      )}

      {/* Add/Edit Form */}
      {showAddForm && (
        <div style={{
          background: 'white',
          padding: '1.5rem',
          borderRadius: '8px',
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
          marginBottom: '2rem'
        }}>
          <h2 style={{ marginTop: '0', color: '#333' }}>
            {editingId ? 'Edit Status Mapping' : 'Add New Status Mapping'}
          </h2>
          <form onSubmit={handleSubmit}>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '1rem', marginBottom: '1rem' }}>
              <div>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>
                  CRM Status *
                </label>
                <input
                  type="text"
                  value={formData.crm_status}
                  onChange={(e) => setFormData({ ...formData, crm_status: e.target.value })}
                  required
                  disabled={editingId !== null}
                  style={{
                    width: '100%',
                    padding: '0.5rem',
                    border: '1px solid #e0e0e0',
                    borderRadius: '4px',
                    boxSizing: 'border-box'
                  }}
                />
              </div>
              <div>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>
                  Client-Friendly Status *
                </label>
                <input
                  type="text"
                  value={formData.client_status}
                  onChange={(e) => setFormData({ ...formData, client_status: e.target.value })}
                  required
                  style={{
                    width: '100%',
                    padding: '0.5rem',
                    border: '1px solid #e0e0e0',
                    borderRadius: '4px',
                    boxSizing: 'border-box'
                  }}
                />
              </div>
            </div>

            <div style={{ marginBottom: '1rem' }}>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>
                Description
              </label>
              <textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                rows={3}
                style={{
                  width: '100%',
                  padding: '0.5rem',
                  border: '1px solid #e0e0e0',
                  borderRadius: '4px',
                  boxSizing: 'border-box',
                  fontFamily: 'inherit'
                }}
              />
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '1rem', marginBottom: '1rem' }}>
              <div>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>
                  FAQ Tag
                </label>
                <input
                  type="text"
                  value={formData.faq_tag}
                  onChange={(e) => setFormData({ ...formData, faq_tag: e.target.value })}
                  style={{
                    width: '100%',
                    padding: '0.5rem',
                    border: '1px solid #e0e0e0',
                    borderRadius: '4px',
                    boxSizing: 'border-box'
                  }}
                />
              </div>
              <div>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>
                  Display Order
                </label>
                <input
                  type="number"
                  value={formData.display_order}
                  onChange={(e) => setFormData({ ...formData, display_order: parseInt(e.target.value) })}
                  style={{
                    width: '100%',
                    padding: '0.5rem',
                    border: '1px solid #e0e0e0',
                    borderRadius: '4px',
                    boxSizing: 'border-box'
                  }}
                />
              </div>
              <div>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>
                  Active Status
                </label>
                <select
                  value={formData.is_active ? 'true' : 'false'}
                  onChange={(e) => setFormData({ ...formData, is_active: e.target.value === 'true' })}
                  style={{
                    width: '100%',
                    padding: '0.5rem',
                    border: '1px solid #e0e0e0',
                    borderRadius: '4px',
                    boxSizing: 'border-box'
                  }}
                >
                  <option value="true">Active</option>
                  <option value="false">Inactive</option>
                </select>
              </div>
            </div>

            <div style={{ display: 'flex', gap: '0.5rem' }}>
              <button
                type="submit"
                style={{
                  padding: '0.75rem 1.5rem',
                  background: '#4A90E2',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontWeight: '500'
                }}
              >
                {editingId ? 'Update' : 'Create'}
              </button>
              <button
                type="button"
                onClick={handleCancel}
                style={{
                  padding: '0.75rem 1.5rem',
                  background: 'white',
                  color: '#666',
                  border: '1px solid #e0e0e0',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontWeight: '500'
                }}
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      {/* List of Mappings */}
      <div style={{
        background: 'white',
        borderRadius: '8px',
        boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
        overflow: 'hidden'
      }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ background: '#f5f5f5', borderBottom: '2px solid #e0e0e0' }}>
              <th style={{ padding: '1rem', textAlign: 'left', fontWeight: '600' }}>CRM Status</th>
              <th style={{ padding: '1rem', textAlign: 'left', fontWeight: '600' }}>Client Status</th>
              <th style={{ padding: '1rem', textAlign: 'left', fontWeight: '600' }}>Description</th>
              <th style={{ padding: '1rem', textAlign: 'center', fontWeight: '600' }}>Active</th>
              <th style={{ padding: '1rem', textAlign: 'center', fontWeight: '600' }}>FAQ Tag</th>
              <th style={{ padding: '1rem', textAlign: 'center', fontWeight: '600' }}>Order</th>
              <th style={{ padding: '1rem', textAlign: 'right', fontWeight: '600' }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {mappings.map((mapping) => (
              <tr key={mapping.id} style={{ borderBottom: '1px solid #e0e0e0' }}>
                <td style={{ padding: '1rem' }}>{mapping.crm_status}</td>
                <td style={{ padding: '1rem', fontWeight: '500' }}>{mapping.client_status}</td>
                <td style={{ padding: '1rem', fontSize: '0.85rem', color: '#666' }}>
                  {mapping.description || '-'}
                </td>
                <td style={{ padding: '1rem', textAlign: 'center' }}>
                  <span style={{
                    padding: '0.25rem 0.75rem',
                    borderRadius: '12px',
                    fontSize: '0.8rem',
                    fontWeight: '600',
                    background: mapping.is_active ? '#e3f2fd' : '#f5f5f5',
                    color: mapping.is_active ? '#4A90E2' : '#999'
                  }}>
                    {mapping.is_active ? 'Active' : 'Inactive'}
                  </span>
                </td>
                <td style={{ padding: '1rem', textAlign: 'center', fontSize: '0.85rem' }}>
                  {mapping.faq_tag || '-'}
                </td>
                <td style={{ padding: '1rem', textAlign: 'center' }}>{mapping.display_order}</td>
                <td style={{ padding: '1rem', textAlign: 'right' }}>
                  <button
                    onClick={() => handleEdit(mapping)}
                    style={{
                      padding: '0.4rem 0.8rem',
                      background: '#4A90E2',
                      color: 'white',
                      border: 'none',
                      borderRadius: '4px',
                      cursor: 'pointer',
                      fontSize: '0.85rem',
                      marginRight: '0.5rem'
                    }}
                  >
                    Edit
                  </button>
                  <button
                    onClick={() => handleDelete(mapping.id)}
                    style={{
                      padding: '0.4rem 0.8rem',
                      background: '#c62828',
                      color: 'white',
                      border: 'none',
                      borderRadius: '4px',
                      cursor: 'pointer',
                      fontSize: '0.85rem'
                    }}
                  >
                    Delete
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default StatusMappings;
